import { makeAutoObservable, runInAction } from "mobx";
import { Case, CaseActionEvent, toCustomerCase } from "../api/dataModel";
import { customerApiBaseUrl, CustomerEntry, CustomerSession } from "../apiClient/ApiClient";
import { RootStore } from ".";

const REFRESH_STORAGE_KEY = "rsa-customer-session-refresh";

export default class CasesStore {
  private rootStore: RootStore;
  private refreshHandle: number | null = null;
  private refreshPromise: Promise<string | null> | null = null;
  private resumePromise: Promise<boolean> | null = null;
  private refreshToken: string | null = null;

  requestUrl: string = customerApiBaseUrl();
  authToken: string | null = null;
  authTokenExp: number | null = null;
  case: Case | null = null;
  latestEvent: CaseActionEvent | null = null;
  events: CaseActionEvent[] = [];
  sessionState: "idle" | "loading" | "ready" | "error" = "idle";
  sessionVersion = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.clearLegacyStorage();
    this.refreshToken = this.storedRefreshToken();
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get presentation() {
    return this.case?.presentation ?? null;
  }

  get brand() {
    return this.presentation?.brand ?? "standard";
  }

  get layoutTheme(): "volvia" | "polestar" | "hedin" | "standard" {
    const theme = this.presentation?.theme;
    return theme === "volvia" || theme === "polestar" || theme === "hedin"
      ? theme
      : "standard";
  }

  get isCaseLoaded() {
    return this.sessionState === "ready" && this.case !== null;
  }

  setRequestUrl(url: string) {
    this.requestUrl = url;
  }

  async openLink(entry: CustomerEntry): Promise<boolean> {
    const version = this.resetSession("loading");
    try {
      await this.rootStore.signalrClient.closeConnection();
      if (version !== this.sessionVersion) return false;
      const session = await this.rootStore.apiClient.exchange(entry, this.requestUrl);
      if (version !== this.sessionVersion) return false;
      this.acceptSession(session);
      const status = await this.loadStatus(version);
      if (!status || version !== this.sessionVersion) return false;
      runInAction(() => {
        this.setCase(status);
        this.sessionState = "ready";
      });
      return true;
    } catch {
      if (version === this.sessionVersion) this.invalidateSession();
      return false;
    }
  }

  resumeSession(): Promise<boolean> {
    if (this.isCaseLoaded) return Promise.resolve(true);
    if (this.resumePromise) return this.resumePromise;
    if (!this.refreshToken) {
      this.refreshToken = this.storedRefreshToken();
    }
    if (!this.refreshToken) {
      this.invalidateSession();
      return Promise.resolve(false);
    }

    const version = this.sessionVersion;
    this.sessionState = "loading";
    const attempt = (async () => {
      const token = await this.refreshSession();
      if (!token || version !== this.sessionVersion) return false;
      try {
        const status = await this.loadStatus(version);
        if (!status || version !== this.sessionVersion) return false;
        runInAction(() => {
          this.setCase(status);
          this.sessionState = "ready";
        });
        return true;
      } catch {
        if (version === this.sessionVersion) this.invalidateSession();
        return false;
      }
    })();
    this.resumePromise = attempt;
    void attempt.finally(() => {
      if (this.resumePromise === attempt) this.resumePromise = null;
    });
    return attempt;
  }

  async refreshSession(): Promise<string | null> {
    if (this.refreshPromise) return this.refreshPromise;
    const handle = this.refreshToken ?? this.storedRefreshToken();
    if (!handle) {
      this.invalidateSession();
      return null;
    }
    const version = this.sessionVersion;
    const attempt = (async () => {
      try {
        const session = await this.rootStore.apiClient.refresh(handle, this.requestUrl);
        if (version !== this.sessionVersion) return null;
        runInAction(() => this.acceptSession(session));
        return session.accessToken;
      } catch {
        if (version === this.sessionVersion) this.invalidateSession();
        return null;
      }
    })();
    this.refreshPromise = attempt;
    try {
      return await attempt;
    } finally {
      if (this.refreshPromise === attempt) this.refreshPromise = null;
    }
  }

  async getAccessToken(): Promise<string> {
    if (this.authToken && (!this.authTokenExp ||
        this.authTokenExp - Date.now() / 1000 > 15)) {
      return this.authToken;
    }
    const token = await this.refreshSession();
    if (!token) throw new Error("Customer session unavailable");
    return token;
  }

  applyUpdate(value: unknown, version: number) {
    if (this.sessionState !== "ready" || !this.authToken ||
        version !== this.sessionVersion) return;
    try {
      this.setCase(toCustomerCase(value));
    } catch {
      this.invalidateSession();
    }
  }

  invalidateSession() {
    this.resetSession("error");
    void this.rootStore.signalrClient.closeConnection();
  }

  dispose() {
    if (this.refreshHandle !== null) window.clearTimeout(this.refreshHandle);
  }

  private resetSession(state: "idle" | "loading" | "error"): number {
    this.sessionVersion += 1;
    this.authToken = null;
    this.authTokenExp = null;
    this.refreshToken = null;
    this.case = null;
    this.latestEvent = null;
    this.events = [];
    this.sessionState = state;
    if (this.refreshHandle !== null) window.clearTimeout(this.refreshHandle);
    this.refreshHandle = null;
    this.refreshPromise = null;
    this.resumePromise = null;
    window.sessionStorage.removeItem(REFRESH_STORAGE_KEY);
    document.body.style.backgroundColor = "";
    return this.sessionVersion;
  }

  private acceptSession(session: CustomerSession) {
    this.refreshToken = session.refreshToken;
    window.sessionStorage.setItem(REFRESH_STORAGE_KEY, session.refreshToken);
    this.authToken = session.accessToken;
    try {
      const encoded = session.accessToken.split(".")[1];
      const payload = JSON.parse(atob(encoded.replace(/-/g, "+").replace(/_/g, "/")));
      this.authTokenExp = typeof payload.exp === "number" ? payload.exp : null;
    } catch {
      this.authTokenExp = null;
    }
    if (this.refreshHandle !== null) window.clearTimeout(this.refreshHandle);
    const expiryDelay = this.authTokenExp
      ? (this.authTokenExp - Date.now() / 1000 - 30) * 1000
      : 60000;
    this.refreshHandle = window.setTimeout(() => {
      void this.refreshSession();
    }, Math.max(2000, Math.min(expiryDelay, 60000)));
  }

  private async loadStatus(version: number): Promise<Case | null> {
    if (!this.authToken || version !== this.sessionVersion) return null;
    try {
      return await this.rootStore.apiClient.getStatus(this.authToken, this.requestUrl);
    } catch (error: any) {
      if (error?.response?.status !== 401 || version !== this.sessionVersion) throw error;
      const token = await this.refreshSession();
      return token && version === this.sessionVersion
        ? this.rootStore.apiClient.getStatus(token, this.requestUrl)
        : null;
    }
  }

  private setCase(value: Case) {
    this.case = value;
    this.events = value.actions.flatMap(action => action.events);
    this.latestEvent = value.actions[0]?.events.slice(-1)[0] ?? null;
  }

  private storedRefreshToken(): string | null {
    return window.sessionStorage.getItem(REFRESH_STORAGE_KEY);
  }

  private clearLegacyStorage() {
    window.localStorage.removeItem("RsaDataUi-CasesStore");
    for (let index = window.localStorage.length - 1; index >= 0; index--) {
      const key = window.localStorage.key(index);
      if (key?.startsWith("eventCompleted")) window.localStorage.removeItem(key);
    }
  }
}
